import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHeader from "./PageHeader";
import { useSiteContent } from "../../context/SiteContext";
import { useCart } from "../../context/CartContext";
import { ShoppingCart, ShoppingBag, X, Plus, Minus, Trash2, ArrowUpRight, Check, CreditCard, Music2 } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "https://effortless-metaphor-api.ogtommype.workers.dev";

function ProductCard({ product, onAdd }) {
  const sizes = product.sizes || [];
  const [size, setSize] = useState(sizes[0] || "");
  const imgs = (product.imageUrls && product.imageUrls.length ? product.imageUrls : (product.imageUrl ? [product.imageUrl] : []));
  const [active, setActive] = useState(0);
  const soldOut = product.available === false;

  return (
    <div className="em-card group flex flex-col" data-testid={`product-${product.id}`}>
      <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-br from-[#111111] to-[#0A0A0A]">
        {imgs[active] ? (
          <img src={imgs[active]} alt={product.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <ShoppingBag size={64} className="text-neon/20" />
          </div>
        )}
        {product.category && (
          <div className="absolute top-3 left-3 label-mono text-neutral-300 border border-[#27272A] px-2 py-1 bg-[#0A0A0A]/70 uppercase">
            {product.category}
          </div>
        )}
        {soldOut && (
          <div className="absolute inset-0 bg-[#0A0A0A]/70 flex items-center justify-center">
            <span className="label-mono text-neon border border-neon/40 px-3 py-1">SOLD OUT</span>
          </div>
        )}
      </div>
      {imgs.length > 1 && (
        <div className="flex gap-2 p-3 pb-0">
          {imgs.map((u, i) => (
            <button
              key={i}
              onMouseEnter={() => setActive(i)}
              onClick={() => setActive(i)}
              className={`w-16 h-20 overflow-hidden border transition-colors ${i === active ? "border-neon" : "border-[#27272A] hover:border-neon/60"}`}
              aria-label={`View image ${i + 1}`}
            >
              <img src={u} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <div className="p-5 border-t border-[#27272A] flex flex-col flex-1">
        <div className="font-display font-bold text-lg group-hover:text-neon transition-colors">{product.name}</div>
        {product.description && (
          <p className="text-neutral-400 text-sm mt-1 leading-relaxed flex-1">{product.description}</p>
        )}
        <div className="font-mono text-2xl text-neon mt-4">${Number(product.price).toFixed(2)}</div>

        {sizes.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {sizes.map((s) => (
              <button
                key={s}
                onClick={() => setSize(s)}
                className={`label-mono px-3 py-1 border transition-colors ${
                  size === s ? "border-neon text-neon" : "border-[#27272A] text-neutral-400 hover:border-neon/60"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <button
          onClick={() => onAdd(product, size)}
          disabled={soldOut}
          data-testid={`add-${product.id}`}
          className="btn-primary mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ShoppingCart size={14} /> {soldOut ? "SOLD OUT" : "ADD TO CART"}
        </button>
      </div>
    </div>
  );
}

function paymentLinks(payments, total) {
  const p = payments || {};
  const amt = total.toFixed(2);
  const out = [];
  if (p.stripe?.enabled) out.push({ key: "stripe", label: "Pay with card", sub: "Secure checkout via Stripe", icon: "card" });
  if (p.paypal?.enabled && p.paypal.handle)
    out.push({ key: "paypal", label: "PayPal", sub: p.paypal.handle, href: `https://paypal.me/${p.paypal.handle.replace(/^@/, "")}/${amt}` });
  if (p.cashapp?.enabled && p.cashapp.cashtag)
    out.push({ key: "cashapp", label: "Cash App", sub: p.cashapp.cashtag, href: `https://cash.app/${p.cashapp.cashtag.startsWith("$") ? p.cashapp.cashtag : "$" + p.cashapp.cashtag}/${amt}` });
  if (p.venmo?.enabled && p.venmo.handle)
    out.push({ key: "venmo", label: "Venmo", sub: p.venmo.handle, href: `https://venmo.com/u/${p.venmo.handle.replace(/^@/, "")}` });
  if (p.zelle?.enabled && p.zelle.handle)
    out.push({ key: "zelle", label: "Zelle", sub: `Send to ${p.zelle.handle}` });
  return out;
}

function CartDrawer({ open, onClose }) {
  const content = useSiteContent();
  const cart = useCart();
  const [stage, setStage] = useState("cart"); // cart | form | payment
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", state: "", zip: "" });
  const [placing, setPlacing] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const methods = paymentLinks(content.payments, cart.total);

  const placeOrder = async (method) => {
    setPlacing(true);
    try {
      const res = await fetch(`${API_URL}/api/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: cart.items,
          total: cart.total,
          customer_name: form.name,
          customer_email: form.email,
          shipping: { address: form.address, city: form.city, state: form.state, zip: form.zip },
          payment_method: method || "pending",
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error || "Order failed"); return null; }
      setOrderId(data.id);
      return data.id;
    } catch {
      toast.error("Order failed");
      return null;
    } finally {
      setPlacing(false);
    }
  };

  const goToPayment = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    const id = await placeOrder();
    if (id) setStage("payment");
  };

  const payStripe = async () => {
    setPlacing(true);
    try {
      const res = await fetch(`${API_URL}/api/checkout/stripe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: cart.items,
          total: cart.total,
          customer_email: form.email,
          currency: content.payments?.currency || "usd",
          orderId,
          successUrl: `${window.location.origin}/store?paid=1`,
          cancelUrl: `${window.location.origin}/store`,
        }),
      });
      const data = await res.json();
      if (res.ok && data.url) { window.location.href = data.url; }
      else { toast.error(data.error || "Stripe unavailable"); }
    } catch {
      toast.error("Stripe unavailable");
    } finally {
      setPlacing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#0A0A0A] border-l border-[#27272A] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[#27272A]">
          <div className="label-mono text-neon flex items-center gap-2">
            <ShoppingCart size={16} /> {stage === "cart" ? "// CART" : stage === "form" ? "// CHECKOUT" : "// PAYMENT"}
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neon"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {cart.items.length === 0 ? (
            <p className="label-mono text-neutral-500 text-center mt-10">// cart empty</p>
          ) : stage === "cart" ? (
            <div className="space-y-4">
              {cart.items.map((it, i) => (
                <div key={i} className="flex gap-3 border border-[#27272A] p-3">
                  <div className="w-16 h-16 flex-shrink-0 bg-[#111] overflow-hidden flex items-center justify-center">
                    {it.imageUrl ? <img src={it.imageUrl} alt="" className="w-full h-full object-cover" /> : <ShoppingBag size={20} className="text-neon/20" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm">{it.name}</div>
                    {it.size && <div className="label-mono text-neutral-500">SIZE {it.size}</div>}
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => cart.setQty(i, it.qty - 1)} className="border border-[#27272A] p-1 hover:border-neon"><Minus size={12} /></button>
                      <span className="font-mono text-sm w-6 text-center">{it.qty}</span>
                      <button onClick={() => cart.setQty(i, it.qty + 1)} className="border border-[#27272A] p-1 hover:border-neon"><Plus size={12} /></button>
                      <span className="font-mono text-neon ml-auto">${(it.price * it.qty).toFixed(2)}</span>
                      <button onClick={() => cart.remove(i)} className="text-red-500 hover:text-red-400 ml-2"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : stage === "form" ? (
            <form onSubmit={goToPayment} className="space-y-3" data-testid="checkout-form">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full bg-[#111] border border-[#27272A] focus:border-neon outline-none px-3 py-2 font-mono text-sm" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-[#111] border border-[#27272A] focus:border-neon outline-none px-3 py-2 font-mono text-sm" />
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Shipping address" className="w-full bg-[#111] border border-[#27272A] focus:border-neon outline-none px-3 py-2 font-mono text-sm" />
              <div className="grid grid-cols-3 gap-2">
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="bg-[#111] border border-[#27272A] focus:border-neon outline-none px-3 py-2 font-mono text-sm" />
                <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State" className="bg-[#111] border border-[#27272A] focus:border-neon outline-none px-3 py-2 font-mono text-sm" />
                <input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="ZIP" className="bg-[#111] border border-[#27272A] focus:border-neon outline-none px-3 py-2 font-mono text-sm" />
              </div>
              <button type="submit" disabled={placing} className="btn-primary w-full justify-center inline-flex items-center gap-2 disabled:opacity-50">
                {placing ? "PLACING..." : "CONTINUE TO PAYMENT"}
              </button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="payment-options">
              <div className="border border-neon/30 p-3">
                <div className="label-mono text-neon">// ORDER {orderId} PLACED</div>
                <p className="text-neutral-400 text-xs mt-1">Complete payment below. Your order is saved and the label has been notified.</p>
              </div>
              {methods.length === 0 && (
                <p className="text-neutral-400 text-sm">No online payment method is configured yet — the label will email you at <span className="text-neon">{form.email}</span> to arrange payment.</p>
              )}
              {methods.map((m) => (
                m.key === "stripe" ? (
                  <button key={m.key} onClick={payStripe} disabled={placing} className="w-full em-card p-4 flex items-center justify-between hover:border-neon group disabled:opacity-50">
                    <div className="flex items-center gap-3"><CreditCard size={18} className="text-neon" /><div className="text-left"><div className="font-display font-bold">{m.label}</div><div className="label-mono text-neutral-500 text-xs">{m.sub}</div></div></div>
                    <ArrowUpRight size={16} className="text-neutral-500 group-hover:text-neon" />
                  </button>
                ) : m.href ? (
                  <a key={m.key} href={m.href} target="_blank" rel="noreferrer" className="w-full em-card p-4 flex items-center justify-between hover:border-neon group">
                    <div className="text-left"><div className="font-display font-bold">{m.label}</div><div className="label-mono text-neutral-500 text-xs">{m.sub} · send ${cart.total.toFixed(2)}</div></div>
                    <ArrowUpRight size={16} className="text-neutral-500 group-hover:text-neon" />
                  </a>
                ) : (
                  <div key={m.key} className="w-full em-card p-4">
                    <div className="font-display font-bold">{m.label}</div>
                    <div className="label-mono text-neutral-500 text-xs mt-1">{m.sub} · send ${cart.total.toFixed(2)} · ref {orderId}</div>
                  </div>
                )
              ))}
              <button onClick={() => { cart.clear(); onClose(); toast.success("Thanks — order recorded."); }} className="btn-secondary w-full justify-center inline-flex items-center gap-2 mt-2">
                <Check size={14} /> I'VE PAID / DONE
              </button>
            </div>
          )}
        </div>

        {cart.items.length > 0 && stage === "cart" && (
          <div className="p-5 border-t border-[#27272A] space-y-3">
            <div className="flex items-center justify-between font-mono">
              <span className="label-mono text-neutral-500">SUBTOTAL</span>
              <span className="text-neon text-xl">${cart.total.toFixed(2)}</span>
            </div>
            <button onClick={() => setStage("form")} className="btn-primary w-full justify-center">CHECKOUT</button>
            <p className="label-mono text-neutral-600 text-xs text-center">// shipping calculated at payment</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Store() {
  const content = useSiteContent();
  const cart = useCart();
  const products = content.products || [];
  const [cat, setCat] = useState("All");
  const [cartOpen, setCartOpen] = useState(false);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [products]);

  const filtered = cat === "All" ? products : products.filter((p) => p.category === cat);

  const onAdd = (product, size) => {
    cart.add(product, size);
    toast.success(`${product.name}${size ? ` (${size})` : ""} added`);
    setCartOpen(true);
  };

  const beats = content.store?.beats || {};

  return (
    <div data-testid="page-store">
      <PageHeader
        code="06"
        label="STORE"
        title="Official Merch"
        subtitle={content.store?.headline || "Rep the label. Premium streetwear and accessories, shipped worldwide."}
      />

      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
          {/* Filters + cart */}
          <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`label-mono px-4 py-2 border transition-colors ${
                    cat === c ? "border-neon text-neon" : "border-[#27272A] text-neutral-400 hover:border-neon/60"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <button onClick={() => setCartOpen(true)} data-testid="open-cart" className="btn-secondary inline-flex items-center gap-2">
              <ShoppingCart size={16} /> CART {cart.count > 0 && <span className="text-neon">({cart.count})</span>}
            </button>
          </div>

          {/* Product grid */}
          {filtered.length === 0 ? (
            <div className="em-card p-16 text-center">
              <p className="label-mono text-neutral-500">// no products yet — add them in /admin → PRODUCTS</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1200px] mx-auto">
              {filtered.map((p) => <ProductCard key={p.id} product={p} onAdd={onAdd} />)}
            </div>
          )}

          {/* Beats link */}
          {beats.url && (
            <a href={beats.url} target="_blank" rel="noreferrer" className="em-card p-6 mt-10 group flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Music2 size={18} className="text-neon" />
                <div>
                  <div className="font-display font-bold group-hover:text-neon transition-colors">{beats.title || "License the beats"}</div>
                  {beats.description && <div className="text-neutral-500 text-sm mt-0.5">{beats.description}</div>}
                </div>
              </div>
              <ArrowUpRight size={20} className="text-neutral-500 group-hover:text-neon" />
            </a>
          )}

          <div className="mt-4">
            <Link to="/beatbox" className="label-mono text-neutral-400 hover:text-neon inline-flex items-center gap-1">
              Browse the beat catalog <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}
